import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useEventListener, useResizeObserver } from 'usehooks-ts';

export interface UseStickyAnchorOptions {
  enabled: boolean;
  /**
   * Anchor px to use (and publish) when no `reference` is provided;
   * `undefined` inherits the published value, `0` publishes zero.
   */
  fallbackPx?: number;
  /**
   * Element whose `parentElement` receives the published CSS variable, so
   * sibling elements inherit the same boundary.
   */
  publishTarget: RefObject<HTMLElement>;
  publishVar: string;
  /**
   * Custom anchor element whose `getBoundingClientRect().bottom` is the anchor px.
   */
  reference?: RefObject<HTMLElement>;
}

/**
 * Computes a sticky anchor in px and publishes it to a CSS variable on the
 * `publishTarget`'s parent so sibling elements can inherit the same boundary.
 * Recomputes on window resize and on resizes of the reference element so the
 * published value doesn't go stale when the reference reflows.
 */
export const useStickyAnchor = ({
  enabled,
  fallbackPx,
  publishTarget,
  publishVar,
  reference,
}: UseStickyAnchorOptions): number => {
  const [anchorPx, setAnchorPx] = useState(0);
  const noopRef = useRef<HTMLElement>(null);

  const recompute = useCallback(() => {
    if (!enabled || !publishTarget.current) {
      setAnchorPx(0);
      return;
    }

    const scope = publishTarget.current.parentElement ?? publishTarget.current;

    if (!reference?.current) {
      if (fallbackPx !== undefined) {
        // scope's own declaration, not getComputedStyle: an inherited value
        // can coincidentally equal fallbackPx and mask a missing write.
        const ownPx = parseFloat(scope.style.getPropertyValue(publishVar));

        if (ownPx !== fallbackPx) {
          scope.style.setProperty(publishVar, `${fallbackPx}px`);
        }

        setAnchorPx(fallbackPx);
        return;
      }

      const currentPx =
        parseFloat(
          getComputedStyle(publishTarget.current).getPropertyValue(publishVar),
        ) || 0;

      setAnchorPx(currentPx);
      return;
    }

    const insetTop = reference.current.getBoundingClientRect().bottom;
    scope.style.setProperty(publishVar, `${insetTop - 0.5}px`);
    setAnchorPx(insetTop);
  }, [enabled, fallbackPx, publishTarget, publishVar, reference]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  // Catches reference position shifts (e.g. content above it reflows).
  useEventListener('resize', recompute);

  // Catches reference size changes without a window resize (e.g. dynamic
  // content loading into the reference, late-loading fonts).
  useResizeObserver({ onResize: recompute, ref: reference ?? noopRef });

  return anchorPx;
};

export default useStickyAnchor;
